/**
 * Wizard Progress Indicator
 *
 * Displays a horizontal step indicator showing the current position
 * within the multi-step onboarding wizard. Steps are numbered 1-4
 * with active, completed, and upcoming visual states.
 */

import { Check } from "lucide-react";
import type { ReactElement } from "react";

export interface WizardStep {
  readonly number: number;
  readonly label: string;
}

interface WizardProgressProps {
  readonly steps: readonly WizardStep[];
  readonly currentStep: number;
}

export function WizardProgress({ steps, currentStep }: WizardProgressProps): ReactElement {
  return (
    <nav aria-label="Onboarding progress" className="mb-8">
      <ol className="flex items-center">
        {steps.map((step, index) => {
          const isCompleted = currentStep > step.number;
          const isActive = currentStep === step.number;
          const isLast = index === steps.length - 1;

          return (
            <li key={step.number} className={`flex items-center ${isLast ? "" : "flex-1"}`}>
              {/* Step circle + label */}
              <div className="flex flex-col items-center">
                <div
                  data-testid={`step-indicator-${step.number}`}
                  className={`flex h-8 w-8 items-center justify-center rounded-full text-sm font-semibold transition-colors ${
                    isCompleted
                      ? "bg-success text-white"
                      : isActive
                        ? "bg-primary text-white"
                        : "bg-background-secondary text-foreground-muted border border-border"
                  }`}
                >
                  {isCompleted ? <Check className="h-4 w-4" /> : step.number}
                </div>
                <span
                  className={`mt-1 text-xs whitespace-nowrap ${
                    isActive
                      ? "font-semibold text-primary"
                      : isCompleted
                        ? "text-success"
                        : "text-foreground-muted"
                  }`}
                >
                  {step.label}
                </span>
              </div>

              {/* Connector line */}
              {!isLast && (
                <div
                  className={`mx-3 mt-[-1rem] h-0.5 flex-1 transition-colors ${
                    isCompleted ? "bg-success" : "bg-border"
                  }`}
                />
              )}
            </li>
          );
        })}
      </ol>
    </nav>
  );
}
